import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { RichText } from '../../components/ui/RichText.js';
import { api } from '../../services/api.js';

/**
 * 160 (US1): the operator edits the two legal documents.
 *
 * Its own Card with its own state and its own mutation, matching how the extension-policy and
 * charge sections on this page are isolated — saving one section never disturbs another.
 *
 * 🔴 The editor is UNCONTROLLED. A `contenteditable` whose HTML is written on every render loses
 * the caret on every keystroke, which makes it unusable for the paragraph-length prose this screen
 * exists to edit. The published text is written into it once, when a document loads; after that the
 * DOM holds the working copy and React holds only what is needed to decide "has this changed?".
 */

const KINDS = [
  { kind: 'terms_of_service', label: 'Terms of Service' },
  { kind: 'privacy_policy', label: 'Privacy Policy' },
] as const;

type Kind = (typeof KINDS)[number]['kind'];

interface LegalDocument {
  id: string;
  kind: Kind;
  version: string;
  body_html: string;
  published_at: string;
}

/** The six formatting actions, as the operator asked for them. */
const TOOLBAR = [
  { label: 'Bold', command: 'bold' },
  { label: 'Italic', command: 'italic' },
  { label: 'Heading', command: 'formatBlock', value: 'h2' },
  { label: 'Bulleted list', command: 'insertUnorderedList' },
  { label: 'Numbered list', command: 'insertOrderedList' },
] as const;

export function LegalDocumentsCard() {
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<Kind>('terms_of_service');
  const editorRef = useRef<HTMLDivElement>(null);

  /** What is currently in the editor, mirrored so the preview and the dirty check can read it. */
  const [draft, setDraft] = useState('');
  /** The published text this draft started from — the dirty check compares against this. */
  const [published, setPublished] = useState('');
  const [version, setVersion] = useState('');
  const [notice, setNotice] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);

  const documentRead = useQuery({
    queryKey: ['legal-document', kind],
    queryFn: () => api.get<LegalDocument>(`/legal-documents/${kind}`),
  });

  // Written into the editor ONCE per loaded document — see the note above about the caret.
  useEffect(() => {
    const body = documentRead.data?.body_html ?? '';
    if (!documentRead.data) return;
    setPublished(body);
    setDraft(body);
    setNotice(null);
    setVersion('');
    // The one `innerHTML` write in this feature, and the only place it is defensible: `body` is
    // what the PLATFORM returned, already sanitised at write time against the allowlist in
    // `backend/src/shared/htmlSanitize.ts`. It is put into an editing surface for a staff member,
    // never into a customer's page — every customer-facing render goes through `RichText`, which
    // builds elements and never injects markup.
    if (editorRef.current) editorRef.current.innerHTML = body;
  }, [documentRead.data]);

  const publish = useMutation({
    mutationFn: (body: { body_html: string; version?: string }) =>
      api.post<LegalDocument>(`/legal-documents/${kind}/revisions`, body),
    onSuccess: (revision) => {
      setPublished(revision.body_html);
      setNotice({ tone: 'ok', text: `Published version ${revision.version}.` });
      queryClient.invalidateQueries({ queryKey: ['legal-document', kind] });
    },
    // 🔴 The editor is NOT cleared and NOT reset here. A failed save must leave the operator's
    // work exactly where it was — losing a page of legal prose to a network blip is the worst
    // thing this screen could do (US1 AC5). The reason shown is the platform's own sentence.
    onError: (error: Error) => setNotice({ tone: 'bad', text: error.message }),
  });

  function applyCommand(command: string, value?: string) {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    if (editorRef.current) setDraft(editorRef.current.innerHTML);
  }

  function insertLink() {
    const href = window.prompt('Link address (https://… or mailto:…)');
    if (!href) return;
    applyCommand('createLink', href);
  }

  function onPublish() {
    if (draft === published) {
      // A no-op save must not become a revision — the history is something a support agent reads
      // back, and a page of identical entries is noise (US1 AC4).
      setNotice({ tone: 'bad', text: 'No changes to publish.' });
      return;
    }
    publish.mutate({ body_html: draft, version: version.trim() || undefined });
  }

  return (
    <Card>
      <h2 className="mb-1 text-base font-semibold text-ink">Legal documents</h2>
      <p className="mb-4 text-sm text-steel">
        What customers read at <code>/terms-of-service</code> and <code>/privacy-policy</code>.
        Publishing creates a new version; previous versions are kept, so a booking always resolves
        to the text its customer agreed to.
      </p>

      <div className="mb-4 flex gap-2">
        {KINDS.map((option) => (
          <Button
            key={option.kind}
            type="button"
            variant={option.kind === kind ? 'primary' : 'secondary'}
            onClick={() => setKind(option.kind)}
          >
            {option.label}
          </Button>
        ))}
      </div>

      {documentRead.isLoading ? (
        <p className="text-sm text-steel">Loading…</p>
      ) : documentRead.isError ? (
        <p className="text-sm text-semantic-danger-text">
          This document could not be loaded. {(documentRead.error as Error).message}
        </p>
      ) : (
        <>
          <p className="mb-3 text-sm text-steel">
            Currently published: version <strong>{documentRead.data?.version}</strong>, since{' '}
            {new Date(documentRead.data?.published_at ?? '').toLocaleDateString()}
          </p>

          <div data-testid="legal-toolbar" className="mb-2 flex flex-wrap gap-2">
            {TOOLBAR.map((action) => (
              <button
                key={action.label}
                type="button"
                aria-label={action.label}
                onClick={() =>
                  applyCommand(action.command, 'value' in action ? action.value : undefined)
                }
                className="rounded border border-hairline px-2 py-1 text-sm text-ink hover:bg-surface-soft"
              >
                {action.label}
              </button>
            ))}
            <button
              type="button"
              aria-label="Link"
              onClick={insertLink}
              className="rounded border border-hairline px-2 py-1 text-sm text-ink hover:bg-surface-soft"
            >
              Link
            </button>
          </div>

          {/* A contenteditable region is exactly what role="textbox" describes, and no HTML element
              carries rich-text editing. `tabIndex` is explicit because biome cannot see that
              contenteditable is natively focusable. */}
          <div
            ref={editorRef}
            data-testid="legal-editor"
            contentEditable
            tabIndex={0}
            role="textbox"
            aria-multiline="true"
            aria-label="Document body"
            onInput={(e) => setDraft((e.currentTarget as HTMLDivElement).innerHTML)}
            className="min-h-64 rounded border border-hairline bg-canvas p-3 text-sm text-ink"
          />

          <div className="mt-4">
            <p className="mb-1 text-sm font-semibold text-ink">Preview</p>
            <p className="mb-2 text-xs text-steel">
              Exactly what the customer sees — formatting the platform cannot publish is already
              removed here.
            </p>
            <div
              data-testid="legal-preview"
              className="rounded border border-hairline bg-surface p-3"
            >
              <RichText html={draft} />
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-ink">
              Version
              <input
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                placeholder="today’s date"
                className="rounded border border-hairline bg-canvas px-2 py-1 text-sm text-ink"
              />
            </label>
            <Button type="button" onClick={onPublish} disabled={publish.isPending}>
              {publish.isPending ? 'Publishing…' : 'Publish'}
            </Button>
            {notice && (
              <span
                className={
                  notice.tone === 'ok'
                    ? 'text-sm text-green-700 dark:text-green-300'
                    : 'text-sm text-semantic-danger-text'
                }
              >
                {notice.text}
              </span>
            )}
          </div>
        </>
      )}
    </Card>
  );
}
